import { TemplateSegment, TemplateSegmentTypeId } from "ng/src/app/templates/shared/template.model";
import { ValidationErrors, ValidatorFn } from "@angular/forms";
import { NoteEntryInput, SectionValidationStatus } from "ng/src/app/notes/shared/note-entry.model";
import { FileAttachment } from "ng/src/app/notes/shared/file-attachments/file-attachment.model";


export class TemplateValueValidators {
	static getValidators(segment: TemplateSegment): TemplateValueValidator<any>[] {
		const validators: TemplateValueValidator<any>[] = [];
		
		if (segment.required)
			validators.push(this.getRequiredValidator());
		
		let additional: ValidatorFn | null = null;
		
		switch (segment.type.id) {
			
			case TemplateSegmentTypeId.FILE_ATTACHMENT:
			case TemplateSegmentTypeId.TAG_CHOICE:
				additional = this.getArraySizeValidator(segment.minRequired, segment.maxAllowed);
				break;
			
			case TemplateSegmentTypeId.NUMERIC_INPUT:
				additional = this.getAbnormalRangeValidator(segment.minValue, segment.maxValue);
				break;
			
			case TemplateSegmentTypeId.TEXT_CHOICE:
				additional = this.getAbnormalValueValidator();
				break;
		}
		
		if (additional)
			validators.push(additional);
		
		return validators;
	}
	
	static getRequiredValidator(): TemplateValueValidator<any> {
		return (value: any) => {
			const requiredError = { required: true } as ValidationErrors;
			if (typeof value === 'string' && value.length === 0) {
				return requiredError;
			}
			else if (Array.isArray(value)) {
				return (value.length === 0) ? requiredError : null;
			}
			return value != null ? null : requiredError;
		}
	}
	
	static getMessageProvider(control: NoteEntryInput): TemplateValueMessageFn {
		const segment = control.templateSegment;
		const rangeMessageFormatter = (value: number) => formatRangeMessage(value, segment.minValue, segment.maxValue);
		
		return function(): TemplateValueMessage | null {
			const errors = control.errors as TemplateValueValidationErrors;
			if (!errors)
				return null;
			
			const message = {
				severity: 'info',
				text: ''
			} as TemplateValueMessage
			
			for (let err of Object.keys(errors)) {
				switch (err) {
					case TemplateValueValidationTypes.REQUIRED:
						message.severity = 'error';
						message.text = 'This value is required.';
						return message;
					
					case TemplateValueValidationTypes.SIZE_LESS_THAN_MIN:
						message.severity = 'error';
						if (segment.minRequired === 1) {
							message.text = `${segment.minRequired} value is required.`
						}
						message.text = `${segment.minRequired} values are required.`
						return message;
					
					case TemplateValueValidationTypes.SIZE_MORE_THAN_MAX:
						message.severity = 'error';
						if (segment.maxAllowed === 1) {
							message.text = `Only 1 value is allowed.`;
						}
						message.text = `No more than ${segment.maxAllowed} values are allowed.`;
						return message;
					
					case TemplateValueValidationTypes.VALUE_LESS_THAN_MIN:
						message.severity = 'warn';
						message.text = segment.belowMinAlertMessage?.length ? segment.belowMinAlertMessage : rangeMessageFormatter(control.value);
						return message;
					
					case TemplateValueValidationTypes.VALUE_MORE_THAN_MAX:
						message.severity = 'warn';
						message.text = segment.aboveMaxAlertMessage?.length ? segment.aboveMaxAlertMessage : rangeMessageFormatter(control.value);
						return message;
						
					case TemplateValueValidationTypes.ABNORMAL_VALUE:
						message.severity = 'warn';
						message.text = 'This value is abnormal';
						return message;
				}
			}
			
			return null;
		}
		
		function formatRangeMessage(value: number, min?: number, max?: number): string {
			const hasMin = typeof min === 'number';
			const hasMax = typeof max === 'number';
			
			if (hasMin && hasMax) {
				return `This value is ${value < min! ? 'below' : 'above'} the normal range of ${min!} to ${max!}.`
			}
			else if (hasMin) {
				return `This value is below the minimum of ${min!}.`;
			}
			else if (hasMax) {
				return `This value is above the maximum of ${max!}.`;
			}
			return '';
		}
	}
	
	static getCommentValidator(noteEntryInput: NoteEntryInput): CommentValueValidator<any> {
		return (errors, comment, attachments) => {
			if (!errors)
				return false;
			
			for (let err of Object.keys(errors as TemplateValueValidationErrors)) {
				switch (err) {
					case TemplateValueValidationTypes.VALUE_MORE_THAN_MAX:
					case TemplateValueValidationTypes.VALUE_LESS_THAN_MIN:
					case TemplateValueValidationTypes.ABNORMAL_VALUE:
						return !comment.trim().length && (attachments?.length ?? 0) === 0;
				}
			}
			
			return false;
		}
	}
	
	private static getAbnormalRangeValidator(min?: number, max?: number): TemplateValueValidator<any> {
		const hasMin = typeof min === 'number';
		const hasMax = typeof max === 'number';
		
		return (value: any): TemplateValueValidationErrors | null => {
			if (typeof value !== "number" || (!hasMin && !hasMax))
				return null;
			
			let errors: TemplateValueValidationErrors = {};
			
			if (hasMin && min! > value)
				errors.valueLessThanMin = true;
			
			if (hasMax && max! < value)
				errors.valueMoreThanMax = true;
			
			return (Object.keys(errors).length ? errors : null);
			
		}
	}
	
	private static getAbnormalValueValidator(): TemplateValueValidator<any> {
		return (item: any): TemplateValueValidationErrors | null => {
			const validate = (value: any) => {
				if (!value?.abnormal)
					return null;
				
				return {
					abnormalValue: true
				} as TemplateValueValidationErrors;
			}
			
			if (Array.isArray(item)) {
				for (const value of item) {
					let errors = validate(value);
					if (errors) return errors;
				}
				return null;
			}
			return validate(item);
		}
	}
	
	private static getArraySizeValidator(min?: number, max?: number): TemplateValueValidator<any> {
		return (value: any): TemplateValueValidationErrors | null => {
			const result: TemplateValueValidationErrors = {};
			
			if (min && min > 0 && (value == null || value.length < min)) {
				result[TemplateValueValidationTypes.SIZE_LESS_THAN_MIN] = true;
			}
			
			if (max && max > 0 && (value == null || value.length > max)) {
				result[TemplateValueValidationTypes.SIZE_MORE_THAN_MAX] = true;
			}
			
			return result;
		}
	}
}

export interface TemplateValueMessageFn {
	(): TemplateValueMessage | null
}

export type TemplateValueMessage = { severity: 'error' | 'warn' | 'info', text: string };

export type TemplateValueValidationErrors = ValidationErrors & Partial<Record<TemplateValueValidationTypes, true>>;

export interface TemplateValueValidator<T> {
	(value: T): TemplateValueValidationErrors | null;
}

export interface CommentValueValidator<T> {
	(errors: TemplateValueValidationErrors | null, comment: string, attachments: FileAttachment[]): boolean;
}

export enum TemplateValueValidationTypes {
	REQUIRED = 'required',
	SIZE_LESS_THAN_MIN = 'sizeLessThanMin',
	SIZE_MORE_THAN_MAX = 'sizeMoreThanMax',
	VALUE_LESS_THAN_MIN = 'valueLessThanMin',
	VALUE_MORE_THAN_MAX = 'valueMoreThanMax',
	ABNORMAL_VALUE = 'abnormalValue'
}

export function validationErrorTypeToStatus(type: TemplateValueValidationTypes): SectionValidationStatus {
	switch (type) {
		case TemplateValueValidationTypes.REQUIRED:
		case TemplateValueValidationTypes.SIZE_LESS_THAN_MIN:
		case TemplateValueValidationTypes.SIZE_MORE_THAN_MAX:
			return SectionValidationStatus.MISSING_DATA;
			
		case TemplateValueValidationTypes.ABNORMAL_VALUE:
		case TemplateValueValidationTypes.VALUE_LESS_THAN_MIN:
		case TemplateValueValidationTypes.VALUE_MORE_THAN_MAX:
			return SectionValidationStatus.ABNORMAL;
	}
}